import './TierExpiryModal.scss';

import React, { useState } from 'react';

import { Button, Modal } from 'antd';

import {
  CalendarOutlined,
  ExclamationCircleFilled,
  InfoCircleFilled,
} from '@ant-design/icons';

import { useAppProvider } from '../../context/AppProvider';
import { TM_SUPPORT_EMAIL } from '../../shared/constants';
import { useAppSelector } from '../../shared/hooks';
import {
  getAccountLossDate,
  getContentLossDate,
  getPlanLabel,
  getTierExpiryView,
  isUAMUser,
} from '../../shared/tierExpiry';
import useRefreshOnScheduledPlanActivation from '../../shared/useRefreshOnScheduledPlanActivation';

const TierExpiryModal: React.FC = () => {
  const user = useAppSelector((state) => state.user);
  const { isTagMango, userTier: creatorTier } = useAppSelector(
    (state) => state.app,
  );
  const { handleLogout } = useAppProvider();
  // The scheduled-plan notice is informational: closing it hides it for this
  // app session (it comes back on the next launch, standing in for the web's
  // always-visible strip).
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  // Mounted once (App.tsx): re-checks the user when a scheduled plan's
  // activation time arrives, so its card clears without reopening the app.
  useRefreshOnScheduledPlanActivation();

  const view = getTierExpiryView(user, creatorTier, isTagMango);
  if (!view) return null;

  if (view.kind === 'scheduledNotice') {
    if (noticeDismissed) return null;
    return (
      <Modal
        open
        footer={null}
        closable
        onCancel={() => setNoticeDismissed(true)}
        centered
        width={520}
        zIndex={100002}
        className="tierExpiryModal tierExpiryModal--scheduled"
        wrapClassName="tierExpiryModal__wrap">
        <div className="tierExpiryModal__header">
          <div className="tierExpiryModal__iconWrap">
            <InfoCircleFilled className="tierExpiryModal__icon" />
          </div>
          <h3 className="tierExpiryModal__title">New Plan Scheduled</h3>
        </div>
        <div className="tierExpiryModal__body">
          <p className="tierExpiryModal__message">{view.message}</p>
          <Button
            type="primary"
            block
            className="tierExpiryModal__gotIt"
            onClick={() => setNoticeDismissed(true)}>
            Got it
          </Button>
        </div>
      </Modal>
    );
  }

  if (view.kind === 'scheduledLocked') {
    // Locked, but a plan is already bought: nothing to renew — the account
    // waits for it. Same blocking shell as the red card; UAM members are sent
    // to their Creator (the subscription is not theirs). The address is
    // written out because `mailto:` does nothing without a mail client.
    return (
      <Modal
        open
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        width={520}
        zIndex={100002}
        className="tierExpiryModal tierExpiryModal--scheduled"
        wrapClassName="tierExpiryModal__wrap">
        <div className="tierExpiryModal__header">
          <div className="tierExpiryModal__iconWrap">
            <CalendarOutlined className="tierExpiryModal__icon" />
          </div>
          <h3 className="tierExpiryModal__title">Plan Activation Scheduled</h3>
        </div>
        <div className="tierExpiryModal__body">
          <p className="tierExpiryModal__message">
            Your new plan is scheduled to become active on{' '}
            {view.plan.activationLabel}.
          </p>
          <p className="tierExpiryModal__support">
            For details or assistance, please contact{' '}
            {isUAMUser(user) ? (
              'your Creator'
            ) : (
              <>
                Support at{' '}
                <a
                  href={`mailto:${TM_SUPPORT_EMAIL}`}
                  className="tierExpiryModal__supportLink">
                  {TM_SUPPORT_EMAIL}
                </a>
              </>
            )}
            .
          </p>
          <p className="tierExpiryModal__logoutText">
            To log out,{' '}
            <button
              type="button"
              className="tierExpiryModal__logoutLink"
              onClick={handleLogout}>
              click here
            </button>
          </p>
        </div>
      </Modal>
    );
  }

  const expiry = user.accountSettingTierSubscriptionExpiry;
  const planLabel = getPlanLabel(creatorTier);
  const accountLossDate = getAccountLossDate(expiry);
  const contentLossDate = getContentLossDate(expiry);

  return (
    <Modal
      open
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={520}
      zIndex={100002}
      className="tierExpiryModal"
      wrapClassName="tierExpiryModal__wrap">
      <div className="tierExpiryModal__header">
        <div className="tierExpiryModal__iconWrap">
          <ExclamationCircleFilled className="tierExpiryModal__icon" />
        </div>
        <h3 className="tierExpiryModal__title">
          Your {planLabel} Plan Has Expired!
        </h3>
        <p className="tierExpiryModal__subtitle">
          Renew now to continue using the platform without interruption.
        </p>
      </div>

      <div className="tierExpiryModal__body">
        <div className="tierExpiryModal__box">
          <p className="tierExpiryModal__boxTitle">
            WHAT HAPPENS IF YOU DON&apos;T RENEW NOW?
          </p>
          <ul className="tierExpiryModal__list">
            <li className="tierExpiryModal__listItem">
              <span className="tierExpiryModal__dayBadge">
                <strong>15</strong>
                <span>DAYS</span>
              </span>
              <span>
                You will lose access to your account.
                {accountLossDate ? (
                  <>
                    <br />
                    <small className="tierExpiryModal__date">
                      on {accountLossDate}
                    </small>
                  </>
                ) : null}
              </span>
            </li>
            <li className="tierExpiryModal__listItem">
              <span className="tierExpiryModal__dayBadge">
                <strong>30</strong>
                <span>DAYS</span>
              </span>
              <span>
                Your subscribers will lose access to your content.
                {contentLossDate ? (
                  <>
                    <br />
                    <small className="tierExpiryModal__date">
                      on {contentLossDate}
                    </small>
                  </>
                ) : null}
              </span>
            </li>
          </ul>
        </div>

        <div className="tierExpiryModal__footer">
          <p className="tierExpiryModal__footerText">
            Renew or change your plan from the web dashboard.
          </p>
          <p className="tierExpiryModal__logoutText">
            For logout,{' '}
            <button
              type="button"
              className="tierExpiryModal__logoutLink"
              onClick={handleLogout}>
              click here
            </button>
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default TierExpiryModal;
